import Tooltip from '@mui/material/Tooltip'
import { useState } from 'react'

import { formatLondonTooltip } from '../utils/londonTooltip'
import {
  CaseDetailsApiResponse,
  formatBool,
  formatDateTime,
  PLACEHOLDER,
  stripHtml,
  valueOrPlaceholder,
} from './case-details-types'

type Field = {
  label: string
  value?: string
  tooltip?: string
}

type Section = {
  id: string
  label: string
  fields: Field[]
}

type AccordionLabels = {
  sectionClassification?: string
  sectionProfile?: string
  sectionProducts?: string
  sectionTracking?: string
  sectionClosure?: string
  fieldSupportType?: string
  fieldSupportLine?: string
  fieldIsAnIncident?: string
  fieldSeverityJustification?: string
  fieldCaseDescription?: string
  fieldCaseOrigin?: string
  fieldProjectNumber?: string
  fieldServiceLevel?: string
  fieldStepsToRecreate?: string
  fieldSubject?: string
  fieldOrderNumber?: string
  fieldSerialNumber?: string
  fieldSiteId?: string
  fieldSubmissionDateTime?: string
  fieldDateTimeClosed?: string
  fieldCaseResolution?: string
  fieldTempSolution?: string
  fieldRejectionReason?: string
}

type ResolvedLabels = Required<AccordionLabels>

const DEFAULT_ACCORDION_LABELS: ResolvedLabels = {
  sectionClassification: 'Case Classification',
  sectionProfile: 'Case Profile',
  sectionProducts: 'Product & Systems',
  sectionTracking: 'Case Tracking',
  sectionClosure: 'Closure Details',
  fieldSupportType: 'Support Type',
  fieldSupportLine: 'Support Line',
  fieldIsAnIncident: 'Is it an incident?',
  fieldSeverityJustification: 'Severity Justification',
  fieldCaseDescription: 'Case Description',
  fieldCaseOrigin: 'Case Origin',
  fieldProjectNumber: 'Project Number',
  fieldServiceLevel: 'Service Level/ Encompass Agreement (SLA)',
  fieldStepsToRecreate: 'Steps To Recreate',
  fieldSubject: 'Subject',
  fieldOrderNumber: 'Order Number',
  fieldSerialNumber: 'Serial Number',
  fieldSiteId: 'Site ID',
  fieldSubmissionDateTime: 'Submission Date/ Time',
  fieldDateTimeClosed: 'Date/Time Closed',
  fieldCaseResolution: 'Case Resolution Description',
  fieldTempSolution: 'Temp Solution/Workaround Description',
  fieldRejectionReason: 'Rejection Reason',
}

function parseAccordionLabels(raw?: string): ResolvedLabels {
  if (!raw) return DEFAULT_ACCORDION_LABELS
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null) {
      return { ...DEFAULT_ACCORDION_LABELS, ...parsed }
    }
  } catch {
    // eslint-disable-next-line no-empty
  }
  return DEFAULT_ACCORDION_LABELS
}

export type CaseDetailsTabProps = {

  apiResponse?: Record<string, unknown> | null

  accordionLabels?: string
}

function buildFallback(lbl: ResolvedLabels): Section[] {
  return [
    {
      id: 'classification',
      label: lbl.sectionClassification,
      fields: [
        { label: lbl.fieldSupportType, value: PLACEHOLDER },
        { label: lbl.fieldSupportLine, value: PLACEHOLDER },
        { label: lbl.fieldIsAnIncident, value: PLACEHOLDER },
      ],
    },
    {
      id: 'profile',
      label: lbl.sectionProfile,
      fields: [
        { label: lbl.fieldSeverityJustification, value: PLACEHOLDER },
        { label: lbl.fieldCaseDescription, value: PLACEHOLDER },
        { label: lbl.fieldCaseOrigin, value: PLACEHOLDER },
        { label: lbl.fieldProjectNumber, value: PLACEHOLDER },
        { label: lbl.fieldServiceLevel, value: PLACEHOLDER },
        { label: lbl.fieldStepsToRecreate, value: PLACEHOLDER },
        { label: lbl.fieldSubject, value: PLACEHOLDER },
      ],
    },
    {
      id: 'products_systems',
      label: lbl.sectionProducts,
      fields: [
        { label: lbl.fieldOrderNumber, value: PLACEHOLDER },
        { label: lbl.fieldSerialNumber, value: PLACEHOLDER },
        { label: lbl.fieldSiteId, value: PLACEHOLDER },
      ],
    },
    {
      id: 'tracking',
      label: lbl.sectionTracking,
      fields: [
        { label: lbl.fieldSubmissionDateTime, value: PLACEHOLDER },
        { label: lbl.fieldDateTimeClosed, value: PLACEHOLDER },
      ],
    },
    {
      id: 'closure',
      label: lbl.sectionClosure,
      fields: [
        { label: lbl.fieldCaseResolution, value: PLACEHOLDER },
        { label: lbl.fieldTempSolution, value: PLACEHOLDER },
        { label: lbl.fieldRejectionReason, value: PLACEHOLDER },
      ],
    },
  ]
}

function buildFromResponse(data: CaseDetailsApiResponse, lbl: ResolvedLabels): Section[] {
  const cls = data.caseClassification ?? {}
  const profile = data.caseProfile ?? {}
  const tracking = data.caseTracking ?? {}
  const closure = data.closureDetails ?? {}
  const products = data.productAndSystems ?? {}
  const caseObj = data.case ?? {}

  return [
    {
      id: 'classification',
      label: lbl.sectionClassification,
      fields: [
        { label: lbl.fieldSupportType, value: valueOrPlaceholder(cls.supportType) },
        { label: lbl.fieldSupportLine, value: valueOrPlaceholder(cls.supportLine) },
        { label: lbl.fieldIsAnIncident, value: formatBool(cls.isAnIncident) },
      ],
    },
    {
      id: 'profile',
      label: lbl.sectionProfile,
      fields: [
        { label: lbl.fieldSeverityJustification, value: valueOrPlaceholder(profile.severityJustification) },
        { label: lbl.fieldCaseDescription, value: stripHtml(profile.caseDescription) },
        { label: lbl.fieldCaseOrigin, value: valueOrPlaceholder(profile.caseOrigin) },
        { label: lbl.fieldProjectNumber, value: valueOrPlaceholder(caseObj.projectNumber) },
        { label: lbl.fieldServiceLevel, value: valueOrPlaceholder(profile.serviceLevelEncompassAgreement || caseObj.serviceAgreement) },
        { label: lbl.fieldStepsToRecreate, value: valueOrPlaceholder(profile.stepsToRecreate) },
        { label: lbl.fieldSubject, value: valueOrPlaceholder(caseObj.subject) },
      ],
    },
    {
      id: 'products_systems',
      label: lbl.sectionProducts,
      fields: [
        { label: lbl.fieldOrderNumber, value: valueOrPlaceholder(products.orderNumber) },
        { label: lbl.fieldSerialNumber, value: valueOrPlaceholder(products.serialNumber) },
        { label: lbl.fieldSiteId, value: valueOrPlaceholder(products.siteId) },
      ],
    },
    {
      id: 'tracking',
      label: lbl.sectionTracking,
      fields: [
        {
          label: lbl.fieldSubmissionDateTime,
          value: formatDateTime(tracking.submissionDateTime),
          tooltip: formatLondonTooltip(tracking.submissionDateTime),
        },
        {
          label: lbl.fieldDateTimeClosed,
          value: formatDateTime(tracking.dateTimeClosed),
          tooltip: formatLondonTooltip(tracking.dateTimeClosed),
        },
      ],
    },
    {
      id: 'closure',
      label: lbl.sectionClosure,
      fields: [
        { label: lbl.fieldCaseResolution, value: stripHtml(closure.caseResolutionDescription) },
        { label: lbl.fieldTempSolution, value: stripHtml(closure.tempSolutionWorkaroundDescription) },
        { label: lbl.fieldRejectionReason, value: valueOrPlaceholder(closure.rejectionReason) },
      ],
    },
  ]
}

const DateFieldTooltip = ({ value, tooltip }: { value: string; tooltip?: string }) => {
  if (!tooltip || value === PLACEHOLDER) return <>{value}</>

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#fff',
            color: '#1a202c',
            fontSize: '13px',
            fontWeight: 600,
            padding: '8px 12px',
            borderRadius: '4px',
            boxShadow: '0 1px 6px rgb(0 0 0 / 30%)',
            whiteSpace: 'nowrap',
            maxWidth: 'none',
          },
        },
        arrow: { sx: { color: '#fff' } },
      }}
    >
      <span>{value}</span>
    </Tooltip>
  )
}

export default function CaseDetailsTab({ apiResponse, accordionLabels }: Readonly<CaseDetailsTabProps>) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    classification: true,
  })

  const toggleSection = (id: string) =>
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }))

  const lbl = parseAccordionLabels(accordionLabels)
  const sections: Section[] = apiResponse
    ? buildFromResponse(apiResponse as CaseDetailsApiResponse, lbl)
    : buildFallback(lbl)

  return (
    <div className="cop-case-details__panel">
      {sections.map(section => {
        const isOpen = !!openSections[section.id]
        return (
          <section
            key={section.id}
            className={`cop-case-details__accordion${isOpen ? ' is-open' : ''}`}
          >
            <button
              type="button"
              className="cop-case-details__accordion-header"
              aria-expanded={isOpen}
              onClick={() => toggleSection(section.id)}
            >
              <span>{section.label}</span>
              <span className="cop-case-details__toggle" aria-hidden="true">
                {isOpen ? '\u2212' : '+'}
              </span>
            </button>
            {isOpen && (
              <div className="cop-case-details__accordion-body">
                <dl className="cop-case-details__fields">
                  {section.fields.map(field => (
                    <div key={field.label} className="cop-case-details__field">
                      <dt className="cop-case-details__field-label">{field.label}</dt>
                      <dd className="cop-case-details__field-value">
                        {field.tooltip ? (
                          <DateFieldTooltip value={field.value || PLACEHOLDER} tooltip={field.tooltip} />
                        ) : (
                          <span dangerouslySetInnerHTML={{ __html: field.value || PLACEHOLDER }} />
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}
